import WebsiteLoader from "@components/website/WebsiteLoader";
import { Workspace } from "@components/shared/workspace/Workspace";
import WebsiteSidebar from "@components/website/sidebar/WebsiteSidebar";
import { WebsitePreview, PageSwitcher } from "@components/website/preview";
import { DomainPicker } from "@components/website/domain-picker";
import type { DomainAssignment } from "@components/website/domain-picker";
import { DeployPill } from "@components/deploy";
import { Label } from "@components/ui/label";
import { PaginationFooter } from "@components/shared/pagination-footer";
import DevResetButton from "@components/shared/DevResetButton";
import { router, usePage } from "@inertiajs/react";
import { useCallback, useState } from "react";
import {
  useWebsiteChatIsInitialLoading,
  useWebsiteAutoInit,
} from "@hooks/website";
import { useDeploys } from "@api/deploys.hooks";
import { useWebsiteId } from "@stores/projectStore";
import { useTrueUser } from "@stores/sessionStore";
import { FirstWebsiteNotification } from "@components/app-notifications";

interface WebsitePageProps {
  website?: { id?: number };
  project?: { id?: number; uuid?: string };
  thread_id?: string;
  [key: string]: unknown;
}

function RestartChatButton() {
  const { website } = usePage<WebsitePageProps>().props;
  const isAdmin = !!useTrueUser()?.admin;
  if (!website?.id) return null;

  // Dev hits the test-only endpoint (no admin required); production is
  // admin-only via the madmin endpoint. Both run the same full pre-build
  // reset (Websites::RestartChat) and the build re-runs on reload.
  if (import.meta.env.DEV) {
    return (
      <DevResetButton
        endpoint={`/test/websites/${website.id}/restart_chat`}
        confirmMessage="Restart chat? This deletes the chat and all checkpoints."
        label="Restart Chat (Dev)"
        loadingLabel="Restarting…"
      />
    );
  }

  if (!isAdmin) return null;

  return (
    <DevResetButton
      forceShow
      endpoint={`/admin/websites/${website.id}/restart_chat`}
      confirmMessage="Restart chat? This deletes the chat, all checkpoints, and the site files — the build re-runs from scratch."
      label="Restart Chat (Admin)"
      loadingLabel="Restarting…"
    />
  );
}

/**
 * Website Build step — generates the website content and, inline in the toolbar,
 * lets the user pick the domain (DomainPicker) and deploy (DeployPill). This is
 * the terminal launch substep; the deploy lives inline rather than on its own
 * page. The footer Continue becomes "Launch Ads" once the site is live.
 *
 * Chat.Root is provided by Website.tsx — this is a pure consumer.
 */
export default function BuildStep() {
  const { project } = usePage<WebsitePageProps>().props;
  const isInitialLoading = useWebsiteChatIsInitialLoading();
  const websiteId = useWebsiteId();

  const [selection, setSelection] = useState<DomainAssignment | null>(null);
  // Lets the Deploy action pop the domain picker when no domain is set yet.
  const [domainPickerOpen, setDomainPickerOpen] = useState(false);

  // Auto-init website generation on first load
  useWebsiteAutoInit();

  // A domain is ready once it's been persisted (claimed/assigned) — a bare AI
  // recommendation pre-selects without an id, so deploy stays gated until the
  // user actually picks one.
  const hasDomain = !!selection?.existingDomainId;

  // The footer Continue activates only after the site is live, where it becomes
  // the "Launch Ads" hand-off (deploy itself happens inline via the pill).
  const { data: deploysData } = useDeploys(project?.id ?? 0, 1);
  const hasLiveDeploy = !!deploysData?.deploys?.some((d) => d.is_live);

  const handleLaunchAds = useCallback(() => {
    router.visit("/ads");
  }, []);

  return (
    <Workspace
      banner={<FirstWebsiteNotification />}
      chat={<WebsiteSidebar />}
      artifacts={
        <>
          {/* The toolbar is always present (Page / site name / Deploy), each with
              its own empty/loading state, so nothing vanishes during the build. */}
          <div className="flex items-start gap-4">
            <div className="flex w-64 shrink-0 flex-col gap-1">
              <Label className="text-xs font-medium text-base-400">Page</Label>
              {websiteId ? <PageSwitcher websiteId={websiteId} /> : null}
            </div>
            <DomainPicker
              selection={selection}
              onSelectionChange={setSelection}
              open={domainPickerOpen}
              onOpenChange={setDomainPickerOpen}
            />
            <div className="ml-auto self-end">
              <DeployPill hasDomain={hasDomain} onNeedsDomain={() => setDomainPickerOpen(true)} />
            </div>
          </div>
          {isInitialLoading ? (
            <div className="border-[#D3D2D0] border rounded-2xl bg-white flex items-center justify-center h-full">
              <WebsiteLoader />
            </div>
          ) : (
            <div className="flex-1 min-h-0">
              <WebsitePreview />
            </div>
          )}
        </>
      }
      footer={
        <PaginationFooter.Root layout="full-bleed" isPending={isInitialLoading}>
          <PaginationFooter.BackButton />
          <PaginationFooter.Actions>
            <RestartChatButton />
            <PaginationFooter.ContinueButton
              onClick={handleLaunchAds}
              disabled={isInitialLoading || !hasLiveDeploy}
            >
              Launch Ads
            </PaginationFooter.ContinueButton>
          </PaginationFooter.Actions>
        </PaginationFooter.Root>
      }
    />
  );
}
